import { formatDateTimeCompact } from '@src/utility/dateFormat';
import { formatLanguageCaption } from '@src/utility/languageCaption';
import { getMostRecentPublishInfo } from '@src/utility/publishInfo';
import { formatResolutionShortLabel } from '@src/utility/videoResolution';
import styled from 'styled-components';

import type { VideoLastPublish } from '../../types.ts/story';

type Props = {
  lastPublishInfo: VideoLastPublish[];
  displayWhenEmpty?: boolean;
  displayPublishInfoInNewline?: boolean;
  emptyMessage?: string;
  /**
   * Names the render's language ahead of the publish facts, for a clip whose
   * publish was a translation (PLAT-11591) — without it "Published 16:9 · 270p"
   * implies the original shipped.
   *
   * Only the clip preview header passes it. The Content Studio clip card
   * deliberately does not: its line is one `nowrap` row in a ~250px column, so
   * the prefix pushed the publisher and date off the card, and the card's
   * aspect-ratio badge already carries the language in its tooltip and download
   * label (PLAT-11591 DS4).
   */
  language?: string | null;
};

const LastPublishedInfo = ({
  lastPublishInfo,
  displayWhenEmpty = true,
  displayPublishInfoInNewline = false,
  emptyMessage,
  language,
}: Props) => {
  if (displayWhenEmpty && !lastPublishInfo.length)
    return <PublishInfo>{emptyMessage}</PublishInfo>;

  if (!lastPublishInfo.length) return null;

  const mostRecent = getMostRecentPublishInfo(lastPublishInfo);
  if (!mostRecent) return null;

  const formattedDateTime = formatDateTimeCompact(mostRecent.timestamp);

  // Format details lead, then the date: "1:1 · 270p · Jul 22 at 3:28 PM".
  const leadingInfo = [
    mostRecent.aspectRatio,
    mostRecent.resolution
      ? formatResolutionShortLabel(mostRecent.resolution)
      : null,
    formattedDateTime,
  ]
    .filter(Boolean)
    .join(' · ');

  // The language goes first, so the thing that identifies *which* version leads
  // the line rather than trailing a run of format details.
  const prefix = language ? `${formatLanguageCaption(language)} · ` : '';

  if (displayPublishInfoInNewline) {
    return (
      <PublishInfo>
        {prefix}Published {leadingInfo}
        {mostRecent.publisher ? `\nby ${mostRecent.publisher}` : ''}
      </PublishInfo>
    );
  }

  // A publish with no recorded publisher drops the tail rather than trailing a
  // dangling "by " — reachable now that the line can describe a PublishRecord,
  // whose `publishedBy` is nullable.
  if (!mostRecent.publisher) {
    const text = `${prefix}Published ${leadingInfo}`;
    return <PublishInfoRow title={text}>{text}</PublishInfoRow>;
  }

  // Single line; when space runs out only the publisher name (the tail)
  // shrinks with an ellipsis so the format/date info stays visible.
  const fullText = `${prefix}Published ${leadingInfo} by ${mostRecent.publisher}`;
  return (
    <PublishInfoRow title={fullText}>
      <FixedSegment>
        {prefix}Published {leadingInfo} by{' '}
      </FixedSegment>
      <PublisherName>{mostRecent.publisher}</PublisherName>
    </PublishInfoRow>
  );
};

export default LastPublishedInfo;

const PublishInfo = styled.div`
  /* De-emphasized metadata: same 11px metrics as the Recent Stories card
     meta lines (HomeStoryCard UpdatedBy/UpdatedDate), with the darkest
     text-role gray (--text-muted = gray-300; gray-400 is a border token and
     unreadable as text on this background). */
  font-size: 11px;
  color: var(--text-muted);
  font-weight: var(--font-weight-normal);
  line-height: 1.4;
  margin-top: 4px;
  white-space: pre-line;
`;

const PublishInfoRow = styled(PublishInfo)`
  display: flex;
  min-width: 0;
  white-space: nowrap;
`;

/* `pre` keeps the literal spaces around "by" and the middot separators. */
const FixedSegment = styled.span`
  flex-shrink: 0;
  white-space: pre;
`;

const PublisherName = styled.span`
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;
